/**
 * Feature Flags
 *
 * Typed accessors for the build-time feature flags exposed through `VITE_*`
 * environment variables. Values are baked into the bundle at Docker build time
 * from AWS SSM Parameter Store (`scripts/get_envs_from_aws_parameters.sh`), so a
 * flag change requires a new build — it is never read from the running pod.
 *
 * Every flag is opt-in: any value other than the exact string `'true'`
 * (including an absent SSM parameter) resolves to `false`.
 *
 * @module featureFlags
 * @category Configuration
 */

/** Literal accepted as "enabled" for every `VITE_*` boolean flag. */
const FLAG_ENABLED = 'true';

/**
 * Whether the federated (SSO/SAML) login flow is enabled.
 *
 * Controlled by `VITE_SSO_FEDERATION_ALLOWED`. Gates the "Acessar via SSO"
 * entry on the login screen, the `/sso` + `/sso/callback` routes and the
 * inbound MinitokenGate. Fail-closed: anything other than the literal string
 * `'true'` (case-insensitive, trimmed) disables the flow. The backend has the
 * matching `SSO_FEDERATION_ALLOWED` flag on the federation endpoints.
 *
 * @returns Whether federated SSO login is allowed
 *
 * @example
 * ```tsx
 * if (isSsoFederationAllowed()) {
 *   // render SSO entry point
 * }
 * ```
 *
 * @since 1.0.0
 */
export const isSsoFederationAllowed = (): boolean =>
  String(import.meta.env?.VITE_SSO_FEDERATION_ALLOWED ?? '')
    .trim()
    .toLowerCase() === FLAG_ENABLED;
